import type {
  BaseSelection,
  BlockComponent,
  Chain,
  EditorHost,
  InitCommandCtx,
} from '@blocksuite/block-std';
import type {
  EdgelessCopilotWidget,
  EdgelessElementToolbarWidget,
  EdgelessRootBlockComponent,
  EdgelessRootService,
  ShapeElementModel,
} from '@blocksuite/blocks';
import {
  AFFINE_EDGELESS_COPILOT_WIDGET,
  BlocksUtils,
  EDGELESS_ELEMENT_TOOLBAR_WIDGET,
  EdgelessTextBlockModel,
  fitContent,
  ImageBlockModel,
  matchFlavours,
  MindmapElementModel,
  NoteBlockModel,
  NoteDisplayMode,
  TextElementModel,
  ViewportPosition,
} from '@blocksuite/blocks';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports
import type { MindmapNode } from '@blocksuite/blocks/dist/surface-block/element-model/utils/mindmap/layout.js';
import type { DisposableGroup } from '@blocksuite/global/utils';
import { Bound } from '@blocksuite/global/utils';
import { PIQUE_BLOCK_FLAVORS } from '@pique/core/components/blocksuite/block-suite-editor/specs/custom/root-block';
import { readProxyArrayValue } from '@pique/core/modules/properties/services/adapter';
import type {
  SerializedAttendee,
  WorkspaceAffineProperties,
} from '@pique/core/modules/properties/services/schema';
import { PageSystemPropertyId } from '@pique/core/modules/properties/services/schema';

import type { PiqueMindmapElementModel } from '../../../../components/blocksuite/block-suite-editor/specs/custom/pique/mindmap-element';
import {
  CATEGORY_TO_TAG_TYPE,
} from '../../../../components/blocksuite/block-suite-editor/specs/custom/pique/overlays/node-categories-config';
import {
  actionItemSelectedSlot,
  attendeeActionsPanelOpenedSlot,
  recordingStartedSlot,
} from '../../../../components/blocksuite/block-suite-editor/specs/custom/pique/pique-slots';
import type { SerializedTodoItem } from '../../../../modules/properties/services/schema';
import { PIQUE_AI_PANEL_WIDGET, type PiqueAIPanelWidget } from '../widgets';
import { getCopilotSelectedElems } from './selection-utils';

export const TEMPLATE_NODES = {
  AGENDA: 'Agenda',
  PREP: 'Prep',
  PEOPLE: 'People',
  TOPIC_PREFIX: 'Topic',
} as const;

export function isTemplateNode(
  nodeText: string,
  templateType: keyof typeof TEMPLATE_NODES
): boolean {
  const trimmedText = nodeText.trim();
  if (templateType === 'TOPIC_PREFIX') {
    return trimmedText.startsWith(TEMPLATE_NODES.TOPIC_PREFIX);
  }
  return trimmedText === TEMPLATE_NODES[templateType];
}

// Helper function to check if a node is a prep-related node (should be stashed during recording)
export function isPrepNode(nodeText: string): boolean {
  return isTemplateNode(nodeText, 'PREP') || isTemplateNode(nodeText, 'PEOPLE');
}

export function mindMapToMarkdown(mindmap: MindmapElementModel) {
  let markdownStr = '';

  const traverse = (
    node: MindmapElementModel['tree']['children'][number],
    indent: number = 0
  ) => {
    const text = (node.element as ShapeElementModel).text?.toString() ?? '';

    markdownStr += `${'  '.repeat(indent)}- ${text}\n`;

    if (node.children) {
      node.children.forEach(node => traverse(node, indent + 2));
    }
  };

  traverse(mindmap.tree, 0);

  return markdownStr;
}

export function isMindMapRoot(ele: BlockSuite.EdgelessModel) {
  const group = ele?.group;

  return group instanceof MindmapElementModel && group.tree?.element === ele;
}

export function isMindmapChild(ele: BlockSuite.EdgelessModel) {
  return ele?.group instanceof MindmapElementModel && !isMindMapRoot(ele);
}

export function getService(host: EditorHost): EdgelessRootService {
  return host.spec.getService('affine:page') as unknown as EdgelessRootService;
}

// Narrow type for the runtime edgeless root element queried from the DOM
interface AffineEdgelessRootElement extends HTMLElement {
  host: EditorHost;
  service: EdgelessRootService;
}

export function getEdgelessCopilotWidget(
  host: EditorHost
): EdgelessCopilotWidget {
  const rootBlockId = host.doc.root?.id as string;
  const copilotWidget = host.view.getWidget(
    AFFINE_EDGELESS_COPILOT_WIDGET,
    rootBlockId
  ) as EdgelessCopilotWidget;

  return copilotWidget;
}

export function determineInsertionBounds(
  host: EditorHost,
  width = 800,
  height = 95
): Bound {
  const service = getService(host);
  const bounds = new Bound(0, 0, width, height);
  const offsetY = 20 / service.viewport.zoom;

  // Try to get copilot selection rect first
  try {
    const copilotWidget = getEdgelessCopilotWidget(host);
    if (
      copilotWidget &&
      copilotWidget.visible &&
      copilotWidget.selectionModelRect
    ) {
      const { x, y, height: h } = copilotWidget.selectionModelRect;
      bounds.x = x;
      bounds.y = y + h + offsetY;
      return bounds;
    }
  } catch (error) {
    // Fallback if copilot not available
  }

  const elements = service.selection.selectedElements;
  if (elements.length) {
    const elementBounds = elements.map(ele => ele.elementBound);
    const commonBound = elementBounds.reduce((acc, bound) => {
      if (!acc) return bound;
      return acc.unite(bound);
    });
    if (commonBound) {
      bounds.x = commonBound.x;
      bounds.y = commonBound.y + commonBound.h + offsetY;
      return bounds;
    }
  }

  // Final fallback to viewport center
  const viewport = service.viewport;
  bounds.x = viewport.centerX - width / 2;
  bounds.y = viewport.centerY - height / 2;

  return bounds;
}

export function findNoteBlockModel(blockElement: BlockComponent) {
  let curBlock = blockElement;
  while (curBlock) {
    if (matchFlavours(curBlock.model, ['affine:note'])) {
      return curBlock.model;
    }
    if (matchFlavours(curBlock.model, ['affine:page', 'affine:surface'])) {
      return null;
    }
    if (!curBlock.parentComponent) {
      break;
    }
    curBlock = curBlock.parentComponent;
  }
  return null;
}

export type MindmapMetadata = {
  nodeText: string;
  path: number[];
  timestamp: string | null;
  category: string | null;
};

export function createMindmapTagElements(
  _host: EditorHost,
  mindmap: PiqueMindmapElementModel,
  timestampData: MindmapMetadata[]
): void {
  if (!mindmap.tree || timestampData.length === 0) {
    return;
  }

  timestampData.forEach(({ path, timestamp, category }) => {
    if (!Array.isArray(path) || path.length <= 1) {
      return;
    }

    const node = mindmap.getNodeByPath(path);
    if (!node?.element?.id) {
      return;
    }

    const normalizedCategory =
      category && category !== 'null' ? category.trim().toLowerCase() : null;
    const mappedCategory =
      normalizedCategory && CATEGORY_TO_TAG_TYPE[normalizedCategory]
        ? CATEGORY_TO_TAG_TYPE[normalizedCategory]
        : normalizedCategory;
    const normalizedTimestamp =
      timestamp && timestamp !== 'null' ? timestamp : null;

    mindmap.setNodeMetadata(node.element.id, {
      category: mappedCategory,
      timestamp: normalizedTimestamp,
    });
  });
}

export function setupAgendaListeners(
  mindmap: PiqueMindmapElementModel,
  disposables: DisposableGroup
): void {
  disposables.add(
    attendeeActionsPanelOpenedSlot.on(() => {
      // zoomToAgendaArea(mindmap);
    })
  );

  disposables.add(
    actionItemSelectedSlot.on(({ actionItem }) => {
      handleActionItemSelection(mindmap, actionItem);
    })
  );

  // Removed transcription auto-completion planning logic

  // Subscribe to recording started events for centering mindmap
  disposables.add(
    recordingStartedSlot.on(() => {
      centerMindmapToViewport(mindmap);

      // Stash prep nodes when recording starts
      if (mindmap.tree) {
        mindmap.tree.children.forEach((child: MindmapNode) => {
          if (child?.element && 'text' in child.element && child.element.text) {
            const nodeText = child.element.text.toString();
            if (isPrepNode(nodeText)) {
              mindmap.stashTree(child.id);
            }
          }
        });
      }
    })
  );
}

export function handleActionItemSelection(
  mindmap: PiqueMindmapElementModel,
  actionItem: SerializedTodoItem
): void {
  if (!mindmap.tree) return;

  let agendaNode: MindmapNode | null = null;
  mindmap.traverse(node => {
    if (node?.element && 'text' in node.element && node.element.text) {
      const nodeText = node.element.text.toString();
      if (isTemplateNode(nodeText, 'AGENDA')) {
        agendaNode = node;
      }
    }
  });

  if (!agendaNode) {
    return;
  }

  updateAgendaWithActionItem(mindmap, agendaNode, actionItem);
}

export function zoomToAgendaArea(mindmap: PiqueMindmapElementModel): void {
  if (!mindmap.tree) return;

  let agendaNode: MindmapNode | null = null;
  mindmap.traverse(node => {
    if (node?.element && 'text' in node.element && node.element.text) {
      const nodeText = node.element.text.toString();
      if (isTemplateNode(nodeText, 'AGENDA')) {
        agendaNode = node;
      }
    }
  });

  if (!agendaNode) {
    return;
  }

  try {
    // Get the edgeless service from the surface
    const edgelessRoot = document.querySelector('affine-edgeless-root');
    if (!edgelessRoot) {
      console.warn('[zoomToAgendaArea] Edgeless root not found, skipping zoom');
      return;
    }

    const edgelessService = (edgelessRoot as unknown as AffineEdgelessRootElement).service;
    if (!edgelessService?.viewport) {
      console.warn(
        '[zoomToAgendaArea] Edgeless service not found, skipping zoom'
      );
      return;
    }

    const subtreeBounds = calculateSubtreeBounds(agendaNode);

    const padding = 100;
    const bound = new Bound(
      subtreeBounds.x - padding,
      subtreeBounds.y - padding,
      subtreeBounds.width + padding * 2,
      subtreeBounds.height + padding * 2
    );

    edgelessService.viewport.setViewportByBound(
      bound,
      [200, 100, 20, 100],
      true
    );
  } catch (error) {
    console.warn('[zoomToAgendaArea] Error during zoom:', error);
  }
}

export function calculateSubtreeBounds(node: MindmapNode): {
  x: number;
  y: number;
  width: number;
  height: number;
} {
  let minX = node.element.x;
  let minY = node.element.y;
  let maxX = node.element.x + node.element.w;
  let maxY = node.element.y + node.element.h;

  // Recursively include all children in the bounds calculation
  const includeNodeInBounds = (currentNode: MindmapNode) => {
    const nodeX = currentNode.element.x;
    const nodeY = currentNode.element.y;
    const nodeMaxX = nodeX + currentNode.element.w;
    const nodeMaxY = nodeY + currentNode.element.h;

    minX = Math.min(minX, nodeX);
    minY = Math.min(minY, nodeY);
    maxX = Math.max(maxX, nodeMaxX);
    maxY = Math.max(maxY, nodeMaxY);

    // Include children
    currentNode.children.forEach(child => includeNodeInBounds(child));
  };

  // Include all children of the agenda node
  node.children.forEach(child => includeNodeInBounds(child));

  return {
    x: minX,
    y: minY,
    width: maxX - minX,
    height: maxY - minY,
  };
}

export function updateAgendaWithActionItem(
  mindmap: PiqueMindmapElementModel,
  agendaNode: MindmapNode,
  actionItem: { title: string }
): void {
  // Find available Topic nodes under Agenda
  const topicNodes = agendaNode.children.filter((child: MindmapNode) => {
    if (child?.element && 'text' in child.element && child.element.text) {
      const nodeText = child.element.text.toString();
      return isTemplateNode(nodeText, 'TOPIC_PREFIX');
    }
    return false;
  });

  topicNodes.sort((a: MindmapNode, b: MindmapNode) => {
    const aText = (a.element as ShapeElementModel).text?.toString().trim() ?? '';
    const bText = (b.element as ShapeElementModel).text?.toString().trim() ?? '';
    const aNum =
      parseInt(aText.replace(TEMPLATE_NODES.TOPIC_PREFIX, '').trim()) || 0;
    const bNum =
      parseInt(bText.replace(TEMPLATE_NODES.TOPIC_PREFIX, '').trim()) || 0;
    return aNum - bNum;
  });

  const firstTopicNode = topicNodes[0];
  if (firstTopicNode) {
    const nodeToUpdate = firstTopicNode;

    const mindmapWithHighlight = mindmap as unknown as { highlightNode?: (id: string, opts?: { duration?: number }) => void };
    mindmapWithHighlight.highlightNode?.(nodeToUpdate.id, { duration: 800 });

    // Update the node text after the highlight animation
    setTimeout(() => {
      mindmap.surface.doc.transact(() => {
        const ytext = (nodeToUpdate.element as ShapeElementModel).text;
        if (ytext) {
          ytext.delete(0, ytext.length);
          ytext.insert(0, actionItem.title);
        }

        // Explicitly fit content to recalculate dimensions
        fitContent(nodeToUpdate.element as ShapeElementModel);
      });
    }, 850);
  } else {
    mindmap.addNode(agendaNode.id, undefined, 'after', {
      text: actionItem.title,
    });
  }
}

export function centerMindmapToViewport(
  mindmap: PiqueMindmapElementModel
): void {
  if (!mindmap.elementBound) return;

  try {
    const edgelessRoot = document.querySelector('affine-edgeless-root');
    if (!edgelessRoot) {
      return;
    }

    const edgelessService = (edgelessRoot as unknown as AffineEdgelessRootElement).service;
    if (!edgelessService?.viewport) {
      return;
    }

    const viewport = edgelessService.viewport;

    // Use the new positioning method if available, otherwise fallback to manual calculation
    if (viewport.setViewportByBoundWithPosition) {
      viewport.setViewportByBoundWithPosition(
        mindmap.elementBound,
        ViewportPosition.TOP_HALF,
        20,
        20,
        true
      );
    } else {
      // Fallback to original method
      const topPadding = 20;
      const horizontalPadding = 20;
      const bottomPadding = viewport.height / 2 + topPadding;

      viewport.setViewportByBound(
        mindmap.elementBound,
        [topPadding, horizontalPadding, bottomPadding, horizontalPadding],
        true
      );
    }
  } catch (error) {
    console.warn('[centerMindmapToViewport] Error during centering:', error);
  }
}

export function getElementToolbar(
  host: EditorHost
): EdgelessElementToolbarWidget {
  const rootBlockId = host.doc.root?.id as string;
  const elementToolbar = host.view.getWidget(
    EDGELESS_ELEMENT_TOOLBAR_WIDGET,
    rootBlockId
  ) as EdgelessElementToolbarWidget;

  return elementToolbar;
}

export function isYoutubeNoteBlock(selected: BlockSuite.EdgelessModel[]) {
  const first = selected[0];
  if (!first) return false;
  const firstChild = first instanceof NoteBlockModel ? first.children[0] : undefined;
  return (
    first instanceof NoteBlockModel &&
    first.children.length === 1 &&
    firstChild !== undefined &&
    BlocksUtils.matchFlavours(firstChild, [
      'affine:embed-pique-youtube',
    ])
  );
}

export function noteBlockOrTextShowWhen(
  _: unknown,
  __: unknown,
  host: EditorHost
) {
  const selected = getCopilotSelectedElems(host);

  return selected.every(
    el =>
      el instanceof NoteBlockModel ||
      el instanceof TextElementModel ||
      el instanceof EdgelessTextBlockModel
  );
}

export function textBlockShowWhen(_: unknown, __: unknown, host: EditorHost) {
  const selected = getCopilotSelectedElems(host);
  return selected.some(
    el => el instanceof TextElementModel || el instanceof EdgelessTextBlockModel
  );
}

export function noteWithCodeBlockShowWen(
  _: unknown,
  __: unknown,
  host: EditorHost
) {
  const selected = getCopilotSelectedElems(host);
  const first = selected[0];
  if (!first) return false;

  const firstChild = first instanceof NoteBlockModel ? first.children[0] : undefined;
  return (
    first instanceof NoteBlockModel &&
    first.children.length === 1 &&
    firstChild !== undefined &&
    BlocksUtils.matchFlavours(firstChild, ['affine:code'])
  );
}

export function noteWithYoutubeBlockShowWen(
  _: unknown,
  __: unknown,
  host: EditorHost
) {
  const selected = getCopilotSelectedElems(host);
  if (!selected.length) return false;

  return isYoutubeNoteBlock(selected);
}

export function noteWithoutPiqueBlocksShowWhen(
  _: unknown,
  __: unknown,
  host: EditorHost
) {
  const selected = getCopilotSelectedElems(host);
  if (!selected.length) return true;

  // Check if any selected note block contains a PIQUE_BLOCK_FLAVOR
  return !selected.some(block => {
    if (!(block instanceof NoteBlockModel)) return false;
    return !block.children.some(child =>
      PIQUE_BLOCK_FLAVORS.some(flavor =>
        BlocksUtils.matchFlavours(child, [flavor as any])
      )
    );
  });
}

export function noteWithoutPiqueBlocksAndMindmapShowWhen(
  _: unknown,
  __: unknown,
  host: EditorHost
) {
  const mindmapChild = mindmapChildShowWhen(_, _, host);
  const mindmapRoot = mindmapRootShowWhen(_, _, host);
  const noteWithoutPiqueBlocks = noteWithoutPiqueBlocksShowWhen(_, _, host);
  return !mindmapChild && !mindmapRoot && noteWithoutPiqueBlocks;
}

export function mindmapChildShowWhen(
  _: unknown,
  __: unknown,
  host: EditorHost
) {
  const selected = getCopilotSelectedElems(host);
  const first = selected[0];
  return selected.length === 1 && first !== undefined && isMindmapChild(first);
}

export function imageOnlyShowWhen(_: unknown, __: unknown, host: EditorHost) {
  const selected = getCopilotSelectedElems(host);
  const first = selected[0];
  return selected.length === 1 && first instanceof ImageBlockModel;
}

export function mindmapRootShowWhen(_: unknown, __: unknown, host: EditorHost) {
  const selected = getCopilotSelectedElems(host);
  const first = selected[0];
  return selected.length === 1 && first !== undefined && isMindMapRoot(first);
}

export function mindmapChildAttendeeShowWhen(
  _: unknown,
  __: unknown,
  host: EditorHost
) {
  const selected = getCopilotSelectedElems(host);
  const first = selected[0];

  if (selected.length !== 1 || !first || !isMindmapChild(first)) {
    return false;
  }

  try {
    const selectedElement = first;
    const groupId = selectedElement.group?.id;

    // TypeScript guard: ensure groupId is not undefined
    if (!groupId) {
      return false;
    }

    const edgelessService = host.std.spec.getService(
      'affine:page'
    ) as unknown as EdgelessRootService;

    const parentMindmap = edgelessService.getElementById(groupId);

    if (parentMindmap instanceof MindmapElementModel) {
      const nodeMap = parentMindmap.nodeMap;
      const doc = host.doc;
      if (nodeMap?.has(selectedElement.id)) {
        const node = nodeMap.get(selectedElement.id);
        const nodeElement = node?.element as { text?: { toString(): string } } | undefined;
        if (nodeElement?.text) {
          const nodeText = nodeElement.text.toString().trim();

          // Get properties from the document
          const propertiesProxy = doc.collection.doc.getMapProxy<
            'affine:workspace-properties',
            WorkspaceAffineProperties
          >('affine:workspace-properties');

          const pageProperties = propertiesProxy.pageProperties?.[doc.id];

          const attendees = readProxyArrayValue<SerializedAttendee>(
            pageProperties?.system[PageSystemPropertyId.Attendees]?.value
          );

          // Check if node text matches any attendee email or name
          return attendees.some(
            (attendee: SerializedAttendee) =>
              attendee.email === nodeText || attendee.name === nodeText
          );
        }
      }
    }
  } catch (err) {
    console.error('Error checking mindmap child attendee:', err);
  }

  return false;
}

export function getPageActionItems(host: EditorHost) {
  try {
    // Get properties from the document
    const propertiesProxy = host.doc.collection.doc.getMapProxy<
      'affine:workspace-properties',
      WorkspaceAffineProperties
    >('affine:workspace-properties');
    const pageProperties = propertiesProxy.pageProperties?.[host.doc.id];
    const actionItems = readProxyArrayValue<SerializedTodoItem>(
      pageProperties?.system[PageSystemPropertyId.ActionItems]?.value
    );

    return actionItems;
  } catch (err) {
    console.error('Error fetching page action items:', err);
    return [];
  }
}

// ===========================
// SHOW WHEN CONDITIONS
// ===========================

export const chainTextBlockShowWhen = (chain: Chain<InitCommandCtx>) => {
  const [_, ctx] = chain
    .getSelectedModels({
      types: ['block', 'text'],
    })
    .run();
  const { selectedModels } = ctx;
  if (!selectedModels || selectedModels.length === 0) return false;

  return selectedModels.some(model =>
    matchFlavours(model, ['affine:paragraph', 'affine:list'])
  );
};

// ===========================
// AI PANEL UTILITIES
// ===========================

export const getAIPanel = (host: EditorHost): PiqueAIPanelWidget | null => {
  const rootBlockId = host.doc.root?.id;
  if (!rootBlockId) {
    return null;
  }
  const aiPanel = host.view.getWidget(PIQUE_AI_PANEL_WIDGET, rootBlockId);
  if (!aiPanel) {
    return null;
  }
  return aiPanel as PiqueAIPanelWidget;
};

export function withAIPanel<T>(
  host: EditorHost,
  callback: (panel: PiqueAIPanelWidget) => T
): T | undefined {
  const panel = getAIPanel(host);
  if (!panel) {
    return undefined;
  }
  return callback(panel);
}

export async function withAIPanelAsync<T>(
  host: EditorHost,
  callback: (panel: PiqueAIPanelWidget) => Promise<T>
): Promise<T | undefined> {
  const panel = getAIPanel(host);
  if (!panel) {
    return undefined;
  }
  return callback(panel);
}

async function ensureSelectionWithFreshNote(
  host: EditorHost,
  service: EdgelessRootService
): Promise<BlockSuite.EdgelessModel[]> {
  const doc = host.doc;
  if (!doc?.root) {
    return service.selection.selectedElements ?? [];
  }

  const hasCanvasContent =
    (service.blocks?.length ?? 0) > 0 || (service.elements?.length ?? 0) > 0;
  const hasNoteBlock = doc.getBlocksByFlavour('affine:note').length > 0;

  if (hasCanvasContent || hasNoteBlock) {
    return service.selection.selectedElements ?? [];
  }

  let noteId: string | null = null;
  const bounds = determineInsertionBounds(host, 800, 95);
  const rootId = doc.root?.id;

  if (!rootId) {
    return service.selection.selectedElements ?? [];
  }

  doc.transact(() => {
    noteId = doc.addBlock(
      'affine:note',
      {
        xywh: bounds.serialize(),
        displayMode: NoteDisplayMode.EdgelessOnly,
      },
      rootId
    );
  });

  if (!noteId) {
    return service.selection.selectedElements ?? [];
  }

  service.selection.set({
    elements: [noteId],
    editing: false,
  });

  try {
    await host.updateComplete;
  } catch (error) {
    console.warn(
      '[getEdgelessAndElements] Failed to await host update after creating a note:',
      error
    );
  }

  return service.selection.selectedElements ?? [];
}

export async function getEdgelessAndElements(host: EditorHost): Promise<{
  edgeless: EdgelessRootBlockComponent | AffineEdgelessRootElement;
  updatedSelectedElements: BlockSuite.EdgelessModel[];
  host: EditorHost;
} | null> {
  if (host.doc.root?.id === undefined) return null;
  const pageService = host.spec.getService(
    'affine:page'
  ) as unknown as EdgelessRootService;
  const edgelessRootService =
    typeof pageService?.getSidebarEdgelessRootService === 'function'
      ? await pageService.getSidebarEdgelessRootService()
      : undefined;

  const getEdgeless = () => {
    const rootId = host.doc.root?.id;
    if (!rootId) {
      return null;
    }

    const edgelessToolBarWidget = host.view.getWidget(
      EDGELESS_ELEMENT_TOOLBAR_WIDGET,
      rootId
    ) as EdgelessElementToolbarWidget;

    if (edgelessToolBarWidget?.edgeless) {
      return {
        edgeless: edgelessToolBarWidget.edgeless,
        selectedElements:
          edgelessToolBarWidget.edgeless.service.selection.selectedElements,
      };
    }

    // Pique Update - If not in edgeless mode, open edgeless in sidebar and attempt to create the equivalent selection in it
    const edgelessRoot = document.querySelector(
      'affine-edgeless-root'
    ) as AffineEdgelessRootElement | null;
    if (edgelessRoot) {
      host = edgelessRoot.host;
      const edgelessService = edgelessRoot.service;
      const baseSelections: BaseSelection[] =
        edgelessService.std.selection.value;
      const selectedBlockIds = baseSelections.map(
        selection => selection.blockId
      );

      const convertedElements = selectedBlockIds
        .map(blockId => {
          const block = edgelessService.doc.getBlockById(blockId);
          if (block) {
            const parentNote = edgelessService.doc.getParent(block);
            if (parentNote && parentNote.flavour === 'affine:note') {
              const noteElement = edgelessService.getElementById(parentNote.id);
              if (noteElement && 'xywh' in noteElement) {
                return noteElement.id;
              }
            }
          }
          return blockId;
        })
        .filter(id => id !== null);

      const edgelessSelectionState = {
        elements: convertedElements,
        editing: false,
        inoperable: false,
      };

      edgelessService.selection.set(edgelessSelectionState);
      return {
        edgeless: edgelessRoot,
        selectedElements: edgelessService.selection.selectedElements,
      };
    }

    return null;
  };

  const result = getEdgeless();
  if (!result || !result.edgeless) {
    return null;
  }

  const { edgeless, selectedElements } = result;
  let updatedSelectedElements = selectedElements;

  if (updatedSelectedElements.length === 0) {
    const isService = (obj: unknown): obj is EdgelessRootService =>
      !!obj &&
      typeof obj === 'object' &&
      'selection' in obj &&
      'viewport' in obj;

    const service: EdgelessRootService | undefined =
      edgelessRootService ??
      (isService(edgeless) ? edgeless : edgeless.service);

    if (service) {
      service.selectAllElements();
      updatedSelectedElements = service.selection.selectedElements ?? [];

      if (updatedSelectedElements.length === 0) {
        updatedSelectedElements = await ensureSelectionWithFreshNote(
          host,
          service
        );
      }
    }
  }

  return {
    edgeless,
    updatedSelectedElements,
    host,
  };
}
